import type { QueueActivityKind } from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { meQuery } from '../../api';
import { queueTodayQuery } from '../../api/ops';
import { Card, cx, EmptyState, formatTimeOfDay } from '../../ui';
import s from '../Ops.module.css';

/** The dot beside each line: decoration only, the words say what happened. */
const DOT: Readonly<Record<QueueActivityKind, string | undefined>> = {
  claim: s.dotClaim,
  approval: s.dotApproval,
  check: s.dotApproval,
  letters: s.dotApproval,
  payroll: s.dotPayroll,
  statement: s.dotPayroll,
  shareworks: s.dotPayroll,
  other: undefined,
};

/**
 * SA1's "Today": what happened on the queue today, newest first: claims, approvals, checks,
 * letters, Payroll's payments and statements. Each line opens its award's record, where the whole
 * history is; the audit log is offered to those who can open it.
 */
export function TodayCard({ teammates }: { teammates: readonly { name: string }[] }): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const today = useQuery(queueTodayQuery);
  const auditLog = me.roles.some((r) => r.role === 'system_admin') && me.preview === null;
  const team =
    teammates.length === 0 ? 'you' : `you and ${teammates.map((t) => t.name).join(' and ')}`;
  return (
    <Card
      title="Today"
      help={{
        shows:
          'Everything that happened on the queue today, newest first: claims, approvals, checks, letters, and Payroll’s payments and statements.',
        canDo:
          'Open an award to see its whole history, including who claimed it and what happened next.',
      }}
      aside={
        auditLog ? (
          <Link href="/settings/system/audit-log" className={s.inlineLink}>
            Full audit trail
          </Link>
        ) : undefined
      }
    >
      {today.isError ? (
        <p className={s.muted}>Today’s activity didn’t load. Reload the page to try again.</p>
      ) : today.data === undefined ? (
        <p className={s.muted}>Loading today’s activity…</p>
      ) : today.data.items.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="calendar"
          size="sm"
          headline="No activity yet today"
          body={`Claims, approvals, letters and Payroll updates from ${team} show here as they happen, newest first.`}
        />
      ) : (
        <>
          <ol className={s.today} aria-label="What happened on the queue today, newest first">
            {today.data.items.map((item) => (
              <li key={item.id} className={s.todayRow}>
                <time className={s.todayTime} dateTime={item.at}>
                  {formatTimeOfDay(item.at)}
                </time>
                <span className={cx(s.todayDot, DOT[item.kind])} aria-hidden="true" />
                <span className={s.todayText}>
                  <Link href={`/awards/${item.award.id}`} className={s.todayLink}>
                    {item.text}
                  </Link>
                  {item.detail !== '' && <span className={s.sub}>{item.detail}</span>}
                </span>
              </li>
            ))}
          </ol>
          {today.data.more && (
            <p className={s.muted}>
              More happened today than fits here. Each award’s record has its whole history.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
